import { useMemo } from 'react';
import { useI18n } from './useI18n';
import {
  formatMoney,
  formatNumber,
  formatPercent,
  formatCompact,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeTime,
  formatList,
  type MoneyValue,
  type FormatMoneyOptions,
  type NumberOptions,
  type DateOptions,
  type RelativeOptions,
} from '../format/index';

export interface UseFormatResult {
  readonly formatMoney: (value: MoneyValue, opts?: FormatMoneyOptions) => string;
  readonly formatNumber: (value: number, opts?: NumberOptions) => string;
  readonly formatPercent: (value: number, opts?: NumberOptions) => string;
  readonly formatCompact: (value: number) => string;
  readonly formatDate: (value: Date, opts?: DateOptions) => string;
  readonly formatTime: (value: Date, opts?: DateOptions) => string;
  readonly formatDateTime: (value: Date, opts?: DateOptions) => string;
  readonly formatRelativeTime: (value: Date, opts?: RelativeOptions) => string;
  readonly formatList: (items: readonly string[], opts?: Intl.ListFormatOptions) => string;
}

export function useFormat(): UseFormatResult {
  const { locale } = useI18n();
  return useMemo(
    () => ({
      formatMoney: (value, opts) => formatMoney(value, locale, opts ?? {}),
      formatNumber: (value, opts) => formatNumber(value, locale, opts ?? {}),
      formatPercent: (value, opts) => formatPercent(value, locale, opts ?? {}),
      formatCompact: (value) => formatCompact(value, locale),
      formatDate: (value, opts) => formatDate(value, locale, opts ?? {}),
      formatTime: (value, opts) => formatTime(value, locale, opts ?? {}),
      formatDateTime: (value, opts) => formatDateTime(value, locale, opts ?? {}),
      formatRelativeTime: (value, opts) => formatRelativeTime(value, locale, opts ?? {}),
      formatList: (items, opts) => formatList(items, locale, opts ?? {}),
    }),
    [locale],
  );
}
